import { useEffect, useState, useCallback } from 'react';
import { getSubscription, cancelSubscription, createSubscription } from '@genfun/core/api/auth';
import { pricingTiers } from '../data/pricing';
import './AccountPopup.css';

// red-site tiers map to backend tier numbers: starter -> 1, premium -> 2
const TIER_NUM = { starter: 1, premium: 2 };
const purchasableTiers = pricingTiers.filter(t => !t.comingSoon);
// Starter is no longer sold, but stays above so existing Starter subscriptions still show its name
const sellableTiers = purchasableTiers.filter(t => t.id !== 'starter');

function tierMeta(num) {
    const t = purchasableTiers.find(pt => TIER_NUM[pt.id] === num);
    if (!t) return { name: `Tier ${num}`, price: '', features: [] };
    return {
        name: t.name,
        price: `$${t.price.current}`,
        period: '/w',
        features: [
            `${t.features.photos} photos`,
            `${t.features.videos} videos`,
            'No watermark',
        ],
    };
}

const STATUS_META = {
    active:    { label: 'Active',    tone: 'good' },
    cancelled: { label: 'Cancelled', tone: 'warn' },
    past_due:  { label: 'Past due',  tone: 'bad'  },
};

function formatDate(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// Subscriptions are weekly (SUB_CYCLE_DAYS=7 on the backend). The backend doesn't return an explicit next-billing
// date, so we derive it as last_rebill_at + 7 days (or started_at + 7 days if
// there hasn't been a rebill yet).
function computeNextBilling(sub) {
    if (!sub) return null;
    const anchor = sub.last_rebill_at || sub.started_at;
    if (!anchor) return null;
    const d = new Date(anchor);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + 7);
    return d;
}

function CloseIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1L11 11M1 11L11 1" stroke="#FFB2C6" strokeWidth="2" strokeLinecap="round"/>
        </svg>
    );
}

export default function AccountPopup({ isOpen, onClose, token, user, refreshUser }) {
    const [sub, setSub] = useState(null);
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [subscribingTier, setSubscribingTier] = useState(null);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!token) { setSub(null); setStatus(null); return; }
        setLoading(true);
        setError(null);
        try {
            const [data, code] = await getSubscription(token);
            setSub(data);
            setStatus(code);
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (!isOpen) return;
        refresh();
    }, [isOpen, refresh]);

    const handleSubscribe = async (tier) => {
        if (!token || subscribingTier !== null) return;
        setSubscribingTier(tier);
        setError(null);
        try {
            const result = await createSubscription(token, tier);
            window.location.href = result.url;
        } catch (e) {
            setError(e.message || String(e));
            setSubscribingTier(null);
        }
    };

    const handleCancel = async () => {
        if (!confirmCancel) { setConfirmCancel(true); return; }
        setCancelling(true);
        setError(null);
        try {
            const [data, code] = await cancelSubscription(token);
            if (code !== 200) {
                setError((data && data.detail) || 'Failed to cancel');
                return;
            }
            setConfirmCancel(false);
            await refresh();
            refreshUser?.();
        } catch (e) {
            setError(e.message || String(e));
        } finally {
            setCancelling(false);
        }
    };

    if (!isOpen) return null;

    const hasSub      = status === 200 && sub;
    const isActive    = hasSub && sub.status === 'active';
    const isCancelled = hasSub && sub.status === 'cancelled';
    const meta        = hasSub ? tierMeta(sub.tier) : null;
    const statusMeta  = hasSub ? (STATUS_META[sub.status] || { label: sub.status, tone: 'warn' }) : null;
    const nextBilling = hasSub && !isCancelled ? computeNextBilling(sub) : null;
    const accessUntil = hasSub && isCancelled ? computeNextBilling(sub) : null;
    const credits     = user?.credits ?? 0;

    const renderPlanCards = (heading) => (
        <div className="acctPlans">
            {heading && <div className="acctPlansIntro">{heading}</div>}
            <div className="acctPlanCards">
                {sellableTiers
                    .filter(p => !hasSub || TIER_NUM[p.id] !== sub.tier)
                    .map(p => {
                        const num = TIER_NUM[p.id];
                        const featured = p.featured;
                        // the first-week price is only charged to users who never had a subscription
                        const intro = !hasSub && p.intro;
                        return (
                            <div key={p.id} className={`acctPlan ${featured ? 'acctPlanFeatured' : ''}`}>
                                {featured && <div className="acctPlanBadge">Best value</div>}
                                <div className="acctPlanName">{p.name}</div>
                                <div className="acctPlanPrice">
                                    ${intro ? intro.price : p.price.current}<span className="acctPlanPer">/w</span>
                                    {intro ? <s>${p.price.current}</s> : (p.price.original && <s>${p.price.original}</s>)}
                                </div>
                                <ul className="acctPlanFeatures">
                                    <li>{p.features.photos} photos</li>
                                    <li>{p.features.videos} videos</li>
                                    <li>No watermark</li>
                                    <li>{intro ? `$${p.price.current}/w after the first week, cancel anytime` : 'Cancel anytime'}</li>
                                </ul>
                                <button
                                    className={`acctPlanBtn ${featured ? 'acctPlanBtnPrimary' : ''}`}
                                    onClick={() => handleSubscribe(num)}
                                    disabled={subscribingTier !== null}
                                >
                                    {subscribingTier === num ? 'Redirecting…' : (hasSub ? `Switch to ${p.name}` : 'Subscribe')}
                                </button>
                            </div>
                        );
                    })}
            </div>
        </div>
    );

    return (
        <div className="acctOverlay" onClick={onClose}>
            <div className="acctPanel" onClick={e => e.stopPropagation()}>
                <button className="acctClose" onClick={onClose} aria-label="Close">
                    <CloseIcon />
                </button>

                <div className="acctHeader">
                    <div className="acctAvatar">
                        {(user?.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="acctIdentity">
                        <div className="acctName">My Account</div>
                        <div className="acctEmail">{user?.email || '—'}</div>
                    </div>
                </div>

                {hasSub && (
                    <div className="acctCreditsCard">
                        <div className="acctCreditsLabel">Credits available</div>
                        <div className="acctCreditsValue">
                            <span className="acctCreditsNum">{credits}</span>
                            <span className="acctCreditsUnit">credits</span>
                        </div>
                        <div className="acctCreditsHint">
                            {isActive
                                ? `Refills with your ${meta.name} plan`
                                : accessUntil
                                    ? `Use them by ${formatDate(accessUntil.toISOString())}`
                                    : 'Use them before your access ends'}
                        </div>
                    </div>
                )}

                <div className="acctSectionTitle">Subscription</div>

                {loading && !sub && <div className="acctLoading">Loading…</div>}

                {!loading && status === 404 && renderPlanCards('Pick a plan — cancel anytime.')}

                {!loading && hasSub && (
                    <div className="acctSubCard">
                        <div className="acctSubTop">
                            <div>
                                <div className="acctSubPlan">{meta.name}</div>
                                <div className="acctSubPrice">
                                    {meta.price}<span className="acctSubPeriod">{meta.period}</span>
                                </div>
                            </div>
                            <span className={`acctStatus acctStatus--${statusMeta.tone}`}>
                                <span className="acctStatusDot" /> {statusMeta.label}
                            </span>
                        </div>

                        <div className="acctSubRows">
                            {sub.started_at && (
                                <div className="acctSubRow">
                                    <span className="acctSubRowLabel">Started</span>
                                    <span className="acctSubRowVal">{formatDate(sub.started_at)}</span>
                                </div>
                            )}
                            {sub.last_rebill_at && (
                                <div className="acctSubRow">
                                    <span className="acctSubRowLabel">Last payment</span>
                                    <span className="acctSubRowVal">{formatDate(sub.last_rebill_at)}</span>
                                </div>
                            )}
                            {nextBilling && (
                                <div className="acctSubRow acctSubRow--accent">
                                    <span className="acctSubRowLabel">Next renewal</span>
                                    <span className="acctSubRowVal">{formatDate(nextBilling.toISOString())}</span>
                                </div>
                            )}
                            {isCancelled && accessUntil && (
                                <div className="acctSubRow acctSubRow--accent">
                                    <span className="acctSubRowLabel">Access until</span>
                                    <span className="acctSubRowVal">{formatDate(accessUntil.toISOString())}</span>
                                </div>
                            )}
                            {sub.cancelled_at && (
                                <div className="acctSubRow">
                                    <span className="acctSubRowLabel">Cancelled on</span>
                                    <span className="acctSubRowVal">{formatDate(sub.cancelled_at)}</span>
                                </div>
                            )}
                        </div>

                        {meta.features?.length > 0 && (
                            <ul className="acctFeatures">
                                {meta.features.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                        )}

                        {isCancelled ? (
                            <div className="acctRenew">
                                <button
                                    className="acctRenewBtn"
                                    onClick={() => handleSubscribe(sub.tier)}
                                    disabled={subscribingTier !== null}
                                >
                                    {subscribingTier === sub.tier
                                        ? 'Redirecting…'
                                        : `Renew ${meta.name} — ${meta.price}${meta.period}`}
                                </button>
                                <div className="acctRenewNote">
                                    You keep access until {accessUntil ? formatDate(accessUntil.toISOString()) : 'the period ends'}.
                                </div>
                            </div>
                        ) : (
                            <div className="acctActions">
                                <button
                                    className={`acctCancel ${confirmCancel ? 'acctCancel--confirm' : ''}`}
                                    onClick={handleCancel}
                                    disabled={cancelling}
                                >
                                    {cancelling
                                        ? 'Cancelling…'
                                        : confirmCancel
                                            ? 'Click again to confirm cancel'
                                            : 'Cancel subscription'}
                                </button>
                                {confirmCancel && !cancelling && (
                                    <button className="acctCancelBack" onClick={() => setConfirmCancel(false)}>
                                        Keep subscription
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {!loading && hasSub && isCancelled && renderPlanCards('or switch plan')}

                {error && <div className="acctError">{error}</div>}
            </div>
        </div>
    );
}
